import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { useReadOnly, ReadOnlyBanner } from "../hooks/useReadOnly";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Trash2, Edit2, X, Upload, Download, Database, Search } from "lucide-react";

const TYPE_OPTS = [
  { value: "exact", label: "Exact", hint: "key = value" },
  { value: "contains", label: "Contains", hint: "substring match" },
  { value: "range", label: "Range", hint: "from ≤ n ≤ to" },
  { value: "regex", label: "Regex", hint: "pattern match" },
  { value: "wildcard", label: "Wildcard", hint: "fallback *" },
];

function Modal({ open, onClose, title, children, width = 620 }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)" }} onClick={onClose} />
      <div style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: width, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
          <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.125rem", margin: 0 }}>{title}</h3>
          <button data-testid="lt-modal-close" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EntryRow({ entry, idx, onChange, onDelete }) {
  const setField = (k, v) => onChange({ ...entry, [k]: v });
  return (
    <div style={{ display: "grid", gridTemplateColumns: "110px 1fr 1fr 1fr 1fr 70px auto", gap: "0.375rem", alignItems: "center", marginBottom: "0.375rem" }}>
      <select data-testid={`lt-entry-type-${idx}`} className="recon-input" style={{ fontSize: "0.8rem" }}
        value={entry.type} onChange={e => setField("type", e.target.value)}>
        {TYPE_OPTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <input data-testid={`lt-entry-key-${idx}`} className="recon-input" style={{ fontSize: "0.8rem", fontFamily: "JetBrains Mono, monospace" }}
        value={entry.key || ""} onChange={e => setField("key", e.target.value)}
        placeholder={entry.type === "range" ? "—" : entry.type === "wildcard" ? "*" : "key / pattern"}
        disabled={entry.type === "range"} />
      <input data-testid={`lt-entry-rf-${idx}`} className="recon-input" style={{ fontSize: "0.8rem", fontFamily: "JetBrains Mono, monospace" }}
        value={entry.range_from || ""} onChange={e => setField("range_from", e.target.value)}
        placeholder="range from" disabled={entry.type !== "range"} />
      <input data-testid={`lt-entry-rt-${idx}`} className="recon-input" style={{ fontSize: "0.8rem", fontFamily: "JetBrains Mono, monospace" }}
        value={entry.range_to || ""} onChange={e => setField("range_to", e.target.value)}
        placeholder="range to" disabled={entry.type !== "range"} />
      <input data-testid={`lt-entry-value-${idx}`} className="recon-input" style={{ fontSize: "0.8rem", fontWeight: 500 }}
        value={entry.value || ""} onChange={e => setField("value", e.target.value)}
        placeholder="resolved value *" />
      <input data-testid={`lt-entry-prio-${idx}`} className="recon-input" style={{ fontSize: "0.8rem", textAlign: "center" }}
        type="number" value={entry.priority ?? 10} onChange={e => setField("priority", parseInt(e.target.value) || 10)} />
      <button data-testid={`lt-entry-del-${idx}`} onClick={onDelete}
        style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function LookupForm({ initial, onSave, onCancel, departments, userRole }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [deptId, setDeptId] = useState(initial?.department_id || "");
  const [entries, setEntries] = useState(initial?.entries || []);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);

  const addEntry = (type = "exact") => setEntries(prev => [...prev, { type, key: "", value: "", priority: type === "wildcard" ? 999 : 10 }]);

  const updateEntry = (idx, updated) => setEntries(prev => prev.map((e, i) => (i === idx ? updated : e)));
  const deleteEntry = (idx) => setEntries(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    for (const e of entries) {
      if (!e.value) { setError("All entries need a resolved value"); return; }
      if (e.type === "range" && (!e.range_from || !e.range_to)) { setError("Range entries need both from and to"); return; }
      if ((e.type === "exact" || e.type === "regex" || e.type === "contains") && !e.key) { setError(`${e.type} entries need a key`); return; }
    }
    setSaving(true);
    try {
      await onSave({ name, description, department_id: deptId || null, entries });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const filtered = filter
    ? entries.map((e, i) => ({ e, i })).filter(({ e }) =>
        (e.key || "").toLowerCase().includes(filter.toLowerCase()) ||
        (e.value || "").toLowerCase().includes(filter.toLowerCase()))
    : entries.map((e, i) => ({ e, i }));

  return (
    <div>
      {error && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", padding: "0.5rem 0.75rem", marginBottom: "0.75rem", fontSize: "0.85rem", color: "#991B1B" }}>{error}</div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: userRole === "superadmin" ? "2fr 2fr 1fr" : "1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
        <div>
          <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Name *</label>
          <input data-testid="lt-name" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
            value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Vendor Code Map" />
        </div>
        <div>
          <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Description</label>
          <input data-testid="lt-description" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
            value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
        </div>
        {userRole === "superadmin" && (
          <div>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Department</label>
            <select data-testid="lt-dept" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
              value={deptId} onChange={e => setDeptId(e.target.value)}>
              <option value="">— All —</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem", marginTop: "0.75rem" }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Entries <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({entries.length})</span>
        </div>
        <div style={{ display: "flex", gap: "0.375rem", alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <Search size={12} style={{ position: "absolute", left: "0.5rem", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
            <input data-testid="lt-filter" className="recon-input" style={{ fontSize: "0.75rem", padding: "0.25rem 0.375rem 0.25rem 1.75rem", width: "140px" }}
              placeholder="Filter..." value={filter} onChange={e => setFilter(e.target.value)} />
          </div>
          {TYPE_OPTS.map(t => (
            <button key={t.value} data-testid={`lt-add-${t.value}`} onClick={() => addEntry(t.value)}
              className="btn-secondary" style={{ fontSize: "0.72rem", padding: "0.25rem 0.5rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <Plus size={10} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {entries.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "110px 1fr 1fr 1fr 1fr 70px auto", gap: "0.375rem", marginBottom: "0.25rem", padding: "0 0.125rem" }}>
          {["Type", "Key / Pattern", "Range From", "Range To", "Value *", "Priority", ""].map(h => (
            <span key={h} style={{ fontSize: "0.65rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{h}</span>
          ))}
        </div>
      )}

      <div style={{ maxHeight: "40vh", overflowY: "auto", padding: "0.125rem" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem", background: "var(--bg-subtle)", borderRadius: "0.5rem" }}>
            No entries yet. Add one using the buttons above.
          </div>
        ) : (
          filtered.map(({ e, i }) => (
            <EntryRow key={i} entry={e} idx={i}
              onChange={(u) => updateEntry(i, u)} onDelete={() => deleteEntry(i)} />
          ))
        )}
      </div>

      <div style={{ marginTop: "1.25rem", display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
        <button data-testid="lt-cancel" onClick={onCancel} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
        <button data-testid="lt-save" onClick={handleSave} disabled={saving} className="btn-primary" style={{ fontSize: "0.85rem" }}>
          {saving ? "Saving..." : "Save Lookup Table"}
        </button>
      </div>
    </div>
  );
}

export default function LookupTablesPage() {
  const readOnly = useReadOnly();
  const { user } = useAuth();
  const [tables, setTables] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | "new" | {id, ...}
  const [importTargetId, setImportTargetId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [ltRes] = await Promise.all([api.get("/lookups/")]);
      setTables(ltRes.data);
      if (user?.role === "superadmin") {
        const d = await api.get("/admin/departments");
        setDepartments(d.data);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (body) => {
    if (editing && editing !== "new" && editing.id) {
      await api.put(`/lookups/${editing.id}`, body);
    } else {
      await api.post("/lookups/", body);
    }
    setEditing(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lookup table? This cannot be undone.")) return;
    await api.delete(`/lookups/${id}`);
    load();
  };

  const handleCsvImport = async (file) => {
    if (!importTargetId || !file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post(`/lookups/${importTargetId}/import-csv`, form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      alert(`Imported ${r.data.imported} entries. ${r.data.errors?.length || 0} errors.`);
      setImportTargetId(null);
      load();
    } catch (e) {
      alert(`Import failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const downloadTemplate = (id) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/lookups/${id}/template`, "_blank");
  };

  return (
    <Layout>
      <div className="animate-in" data-testid="lookup-tables-page">
        <ReadOnlyBanner what="lookup tables" />
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1.5rem" }}>
          <div>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.75rem", color: "var(--text-primary)", margin: 0 }}>
              Lookup Tables
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginTop: "0.25rem" }}>
              Map values during transformation (exact, range, regex, wildcard)
            </p>
          </div>
          {!readOnly && (
            <button data-testid="lt-new-btn" className="btn-primary"
              onClick={() => setEditing("new")}
              style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.85rem" }}>
              <Plus size={14} /> New Lookup Table
            </button>
          )}
        </div>

        <div className="recon-card" style={{ padding: 0, overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>Loading...</div>
          ) : tables.length === 0 ? (
            <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
              <Database size={32} style={{ color: "var(--text-muted)", margin: "0 auto 0.75rem", opacity: 0.4 }} />
              <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", margin: 0 }}>
                No lookup tables yet. <button onClick={() => setEditing("new")} style={{ color: "var(--sage)", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>Create one</button>
              </p>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Name", "Description", "Department", "Entries", "Updated", "Actions"].map(h => (
                    <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tables.map(t => (
                  <tr key={t.id} className="table-row" data-testid={`lt-row-${t.id}`}>
                    <td className="table-cell" style={{ fontWeight: 500 }}>{t.name}</td>
                    <td className="table-cell" style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>{t.description || "—"}</td>
                    <td className="table-cell" style={{ color: "var(--text-muted)" }}>{t.department_name || "—"}</td>
                    <td className="table-cell">
                      <span style={{ background: "var(--sage-light)", color: "var(--sage)", borderRadius: "9999px", padding: "0.125rem 0.5rem", fontSize: "0.75rem", fontWeight: 600 }}>
                        {(t.entries || []).length}
                      </span>
                    </td>
                    <td className="table-cell" style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>
                      {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="table-cell">
                      <div style={{ display: "flex", gap: "0.375rem" }}>
                        <button data-testid={`lt-import-${t.id}`} title="Import CSV"
                          onClick={() => setImportTargetId(t.id)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}>
                          <Upload size={14} />
                        </button>
                        <button data-testid={`lt-template-${t.id}`} title="Download CSV template"
                          onClick={() => downloadTemplate(t.id)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}>
                          <Download size={14} />
                        </button>
                        <button data-testid={`lt-edit-${t.id}`} onClick={() => setEditing(t)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}>
                          <Edit2 size={14} />
                        </button>
                        <button data-testid={`lt-delete-${t.id}`} onClick={() => handleDelete(t.id)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ marginTop: "1.25rem", padding: "0.875rem 1rem", background: "var(--sage-light)", borderRadius: "0.5rem", border: "1px solid var(--sage-border)" }}>
          <p style={{ fontSize: "0.8rem", color: "var(--text-secondary)", margin: 0, lineHeight: 1.6 }}>
            <strong>How lookups work:</strong> Attach a lookup table to any rule as a transform (<em>"lookup: &lt;name&gt;"</em>).
            During matching, each value is resolved in priority order: exact → range → regex → wildcard (fallback). First hit wins.
          </p>
        </div>
      </div>

      <Modal open={editing !== null} onClose={() => setEditing(null)}
        title={editing === "new" ? "New Lookup Table" : `Edit: ${editing?.name || ""}`} width={780}>
        {editing !== null && (
          <LookupForm
            initial={editing === "new" ? null : editing}
            departments={departments}
            userRole={user?.role}
            onSave={handleSave}
            onCancel={() => setEditing(null)} />
        )}
      </Modal>

      <Modal open={importTargetId !== null} onClose={() => setImportTargetId(null)}
        title="Import Entries from CSV" width={440}>
        <p style={{ fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: 1.5 }}>
          Upload a CSV with columns: <strong>type, key, range_from, range_to, value, priority</strong>.
          Entries will be appended to the existing table.
        </p>
        <input data-testid="lt-csv-input" type="file" accept=".csv"
          onChange={(e) => handleCsvImport(e.target.files?.[0])}
          style={{ marginTop: "0.75rem", width: "100%", fontSize: "0.85rem" }} />
        <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border-default)", fontSize: "0.8rem", color: "var(--text-muted)" }}>
          <button onClick={() => importTargetId && downloadTemplate(importTargetId)} className="btn-secondary"
            style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}>
            <Download size={11} /> Download template
          </button>
        </div>
      </Modal>
    </Layout>
  );
}
